import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { format } from 'date-fns';
import { kebabCase } from 'lodash-es';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import {
  getFLSAReportProfiles,
  getFLSAReportTotals,
  downloadFLSAReportCsv as downloadFLSAReportCsvRequest,
} from '@/api/flsaReport';
import { handleCSVDownload } from '@/helpers/fileDownload';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useAppPagination } from '@/stores/app/pagination';
import { useFilters } from '@/stores/filters/filters';
import { FLSA_ALERT_LABELS } from '@/components/library/flsaSurveys/helpers';
import { READABLE_FILE_NAME_FORMAT } from '@/helpers/date';

export const useFlsaSurveysStore = defineStore('flsaSurveys', () => {
  const { setLoading } = useAppLoading();
  const { updateFilters, sendFilterRequest, loadFilters } = useFilters();
  const { addErrorSnackbar } = useAppSnackbar();

  const { selectedSubMenuOption } = storeToRefs(useSubMenu());
  const { fields, mergedTempFilters } = storeToRefs(useFilters());
  const { authData, isStandaloneJobNav } = storeToRefs(useAppAuthData());

  const profiles = ref([]);
  const profileCount = ref(0);
  const totals = ref({});
  const textFilter = ref('');
  const selectedAlertStatuses = ref([]);
  const csvDownloadLoading = ref(false);
  const showCountryDefault = ref(true);
  const viewMode = ref('hris_linked');

  const flsaAlertOptions = computed(() => {
    const options = [
      { id: 'up_to_date', label: FLSA_ALERT_LABELS.up_to_date },
      { id: 'no_flsa_submission', label: FLSA_ALERT_LABELS.no_flsa_submission },
      { id: 'outdated_form', label: FLSA_ALERT_LABELS.outdated_form },
      { id: 'status_mismatch', label: FLSA_ALERT_LABELS.status_mismatch },
      { id: 'review_overdue', label: FLSA_ALERT_LABELS.review_overdue },
    ];

    if (isStandaloneJobNav.value) {
      return options.filter((option) => option.id !== 'status_mismatch');
    }

    return options;
  });

  const getAlertFilterOption = (id) => flsaAlertOptions.value.find((option) => option.id === id);

  const buildAlertTypeFilterField = (fieldsToUse, newValue) => {
    const existingField = fieldsToUse.find((field) => field.id === 'alert_type');
    const newField = existingField
      ? { ...existingField, value: newValue }
      : {
        id: 'alert_type',
        label: 'Alert Status',
        type: 'flatlist',
        async: false,
        asyncUrl: null,
        position: 'column_1',
        alwaysOn: false,
        options: flsaAlertOptions.value,
        category: 'basic',
        value: newValue,
      };

    return existingField
      ? fieldsToUse.map((field) => (field.id === 'alert_type' ? newField : field))
      : [...fieldsToUse, newField];
  };

  const setTextFilter = (value) => {
    textFilter.value = value;
  };

  const resetCountryDefault = () => {
    showCountryDefault.value = true;
  };

  const setViewMode = (mode) => {
    // Published view is hidden on standalone, so always use drafts there.
    viewMode.value = isStandaloneJobNav.value ? 'draft' : mode;
  };

  const buildFlsaReportFilters = (selectedFilters, routeQuery = {}) => ({
    ...(selectedFilters || {}),
    country: routeQuery.country || 'US',
    jurisdiction: 'federal',
    entity_type: viewMode.value === 'draft' ? 'draft_profile' : 'job_profile',
  });

  const getProfileItemByUlid = (ulid) => (
    profiles.value.find((item) => item?.profile?.ulid === ulid) || null
  );

  const loadProfiles = async ({
    routeQuery,
    selectedFilters,
    page,
    searchTerm: newSearchTerm,
  }) => {
    setLoading({ pageLoading: true, listLoading: true });

    try {
      let params = routeQuery || {};

      if (!params.sort && !selectedSubMenuOption.value) {
        params.sort = 'title';
      } else if (selectedSubMenuOption.value) {
        params = { ...params, sort: selectedSubMenuOption.value };
      }

      params.page = page || undefined;

      const {
        items = [], pagination, textFilter: responseTextFilter,
      } = await getFLSAReportProfiles({
        params,
        searchTerm: newSearchTerm,
        selectedFilters: buildFlsaReportFilters(selectedFilters, params),
      });

      profiles.value = items;
      profileCount.value = pagination?.totalDocuments ?? 0;
      useAppPagination().updatePagination(pagination);

      if (responseTextFilter?.value !== undefined) {
        setTextFilter(responseTextFilter.value);
      }
    } catch (err) {
      addErrorSnackbar({
        text: `FLSA survey load error: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ pageLoading: false, listLoading: false });
    }
  };

  const loadTotals = async ({ selectedFilters, routeQuery } = {}) => {
    try {
      const {
        alert_type: _,
        ...filtersWithoutAlert
      } = selectedFilters || {};

      const {
        alert_type: __,
        ...routeQueryWithoutAlert
      } = routeQuery || {};

      const summary = await getFLSAReportTotals({
        selectedFilters: buildFlsaReportFilters(filtersWithoutAlert, routeQueryWithoutAlert),
        params: routeQueryWithoutAlert,
        searchTerm: textFilter.value,
      });

      totals.value = summary || {};
    } catch (err) {
      addErrorSnackbar({
        text: `FLSA survey totals load error: ${err.message || err}`,
      });
      throw err;
    }
  };

  const loadPage = async (params) => {
    const resolvedParams = { ...params };
    const { routeQuery = {} } = resolvedParams;

    resolvedParams.routeQuery = {
      ...routeQuery,
      country: routeQuery.country || 'US',
      jurisdiction: 'federal',
    };

    if (showCountryDefault.value) {
      showCountryDefault.value = false;
    }

    loadProfiles(resolvedParams);
    loadTotals(resolvedParams);
    loadFilters({
      ...resolvedParams,
      selectedFilters: buildFlsaReportFilters(
        resolvedParams.selectedFilters,
        resolvedParams.routeQuery,
      ),
    });
  };

  const downloadCsv = async (routeQuery) => {
    csvDownloadLoading.value = true;

    try {
      let params = routeQuery || {};

      if (!params.sort && !selectedSubMenuOption.value) {
        params.sort = 'title';
      } else if (selectedSubMenuOption.value) {
        params = { ...params, sort: selectedSubMenuOption.value };
      }

      params = {
        ...params,
        country: params.country || 'US',
        jurisdiction: 'federal',
        entity_type: viewMode.value === 'draft' ? 'draft_profile' : 'job_profile',
      };

      const commaSeparatedString = await downloadFLSAReportCsvRequest({
        params,
        selectedFilters: buildFlsaReportFilters(mergedTempFilters.value, params),
        searchTerm: textFilter.value,
      });

      const { projectName } = authData.value;
      const today = format(new Date(), READABLE_FILE_NAME_FORMAT);
      const fileTitle = `${kebabCase(projectName.toLowerCase())}-federal-flsa-dashboard-${today}.csv`;

      handleCSVDownload({
        commaSeparatedString,
        autoClick: true,
        title: fileTitle,
      });
    } catch (error) {
      addErrorSnackbar({ text: `FLSA survey CSV download: ${error.message}` });
      throw error;
    } finally {
      csvDownloadLoading.value = false;
    }
  };

  const updateSelectedAlertStatuses = (optionIds) => {
    if (!optionIds?.length) {
      selectedAlertStatuses.value = [];
      return;
    }

    const ids = isStandaloneJobNav.value
      ? optionIds.filter((id) => id !== 'status_mismatch')
      : optionIds;

    selectedAlertStatuses.value = ids;
  };

  const resetSelectedAlertStatus = (router) => {
    if (!selectedAlertStatuses.value?.length) return;

    selectedAlertStatuses.value = [];

    const fieldsToUse = fields.value || [];
    const newFields = buildAlertTypeFilterField(fieldsToUse, []);

    updateFilters({ filters: newFields });
    sendFilterRequest({ router });
  };

  const selectAlertStatus = (optionIds, router) => {
    const currentAlertIds = [...selectedAlertStatuses.value].sort();
    const targetAlertIds = [...optionIds].sort();
    const fieldsToUse = fields.value || [];

    const isSameSelection = currentAlertIds.length === targetAlertIds.length
      && currentAlertIds.every((id, index) => id === targetAlertIds[index]);

    // Clicking the already-selected card clears the filter, otherwise apply the card's option ids.
    const newIds = isSameSelection ? [] : optionIds;

    selectedAlertStatuses.value = newIds;

    // Clear the search text filter
    setTextFilter('');

    // Get filter objects from optionIds
    const newValue = newIds
      .map((id) => getAlertFilterOption(id))
      .filter(Boolean);

    // Use existing field if it exists, otherwise create a new one to maintain card filter functionality
    const newFields = buildAlertTypeFilterField(fieldsToUse, newValue);

    updateFilters({ filters: newFields });
    sendFilterRequest({ router });
  };

  const reset = () => {
    profiles.value = [];
    profileCount.value = 0;
    totals.value = {};
    textFilter.value = '';
    selectedAlertStatuses.value = [];
    csvDownloadLoading.value = false;
    showCountryDefault.value = true;
    viewMode.value = 'hris_linked';
  };

  return {
    profiles,
    profileCount,
    totals,
    textFilter,
    selectedAlertStatuses,
    csvDownloadLoading,
    viewMode,
    setTextFilter,
    setViewMode,
    getProfileItemByUlid,
    loadPage,
    resetCountryDefault,
    downloadCsv,
    selectAlertStatus,
    resetSelectedAlertStatus,
    updateSelectedAlertStatuses,
    reset,
  };
});
